import Navbar from "components/navbar/Navbar";
import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { findMatching } from "utils/products";
import ResultsList from "./results-list/ResultsList";
import Specs from "./specs/Specs";
import "./Results.scss";
import { setCircles, setOperatorMode, setReduxResults } from "reducer";
import { decode, getCode } from "utils/encodeDecode";
import { Popup } from "components/popups/Popup";
import Icon from "@mdi/react";
import { mdiClose, mdiArrowLeft } from "@mdi/js";
import logo from "../../assets/logo-black.svg";
import TooltipInfo from "components/tooltip-info/TooltipInfo";

const resultsConfiguration = [
  {
    label: "Complete Perfect",
    key: "completePerfect",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches perfectly the configuration required.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Complete Different Thread",
    key: "completeDifferentThread",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number and size of the lines; it differs in the thread type of the quick couplings.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Complete Different Port",
    key: "completeDifferentPort",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number and size of the lines; it differs in the port size of the quick couplings.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Complete Different Port Or Thread",
    key: "completeDifferentPortOrThread",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number and size of the lines; it differs in the port size and the thread type of the quick couplings.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Empty Perfect",
    key: "emptyPerfect",
    subKey: "empty",
    couple: true,
    tooltipText:
      "The proposed solution matches the configuration required for the number and size of the lines.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Empty Total Lines Plus One",
    key: "emptyTotalLinesPlusOne",
    subKey: "empty",
    isNotVisibleIfOneInch: true,
    couple: true,
    tooltipText:
      "The proposed solution matches the configuration required for the size of the lines; the plates have one more line that could be left empty.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Empty Total Lines Plus Two",
    key: "emptyTotalLinesPlusTwo",
    subKey: "empty",
    isNotVisibleIfOneInch: true,
    couple: true,
    tooltipText:
      "The proposed solution matches the configuration required for the size of the lines; the plates have two more lines that could be left empty.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Empty With More Total Lines",
    key: "emptyTotalLinesPlusN",
    subKey: "empty",
    isNotVisiblebByDefault: true,
    couple: true,
    tooltipText:
      "The proposed solution matches the configuration required for the size of the lines; the plates have more lines that could be left empty.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Complete Body Size Plus One",
    key: "completeBodySizePlusOne",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number of the lines; some of the lines are one size bigger than the original configuration.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Complete Body Size Plus One Different Thread",
    key: "completeBodySizePlusOneDifferentThread",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number of the lines; some of the lines are one size bigger than the original configuration and the thread type of the quick couplings is different.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Complete Body Size Plus One Different Port",
    key: "completeBodySizePlusOneDifferentPort",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number of the lines; some of the lines are one size bigger than the requested configuration and the port size of the quick couplings is different.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Complete Body Size Plus One Different Port Or Thread",
    key: "completeBodySizePlusOneDifferentPortOrThread",
    subKey: "complete",
    tooltipText:
      "The proposed multicoupling matches the configuration required for the number of the lines; some of the lines are one size bigger than the requested configuration and the thread type and port size of the quick couplings are different.\n\nThe Multi Complete item is provided with the quick couplings mounted and the protective cap.",
  },
  {
    label: "Empty Body Size Plus One",
    key: "emptyBodySizePlusOne",
    subKey: "empty",
    isNotVisibleIfOneInch: true,
    couple: true,
    tooltipText:
      "The proposed solution matches the configuration required for the number of the lines; some of the lines are one size bigger than the original configuration.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Empty Body Size Plus One Total Lines Plus One",
    key: "emptyBodySizePlusOneTotalLinesPlusOne",
    subKey: "empty",
    isNotVisibleIfOneInch: true,
    couple: true,
    tooltipText:
      "In the proposed solution some of the lines are one size bigger than the original configuration. The plates have one more line that could be left empty.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Empty Body Size Plus One Total Lines Plus Two",
    key: "emptyBodySizePlusOneTotalLinesPlusTwo",
    subKey: "empty",
    isNotVisibleIfOneInch: true,
    couple: true,
    tooltipText:
      "In the proposed solution some of the lines are one size bigger than the original configuration. The plates have two more lines that could be left empty.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
  {
    label: "Empty Body Size Plus One With More Total Lines",
    key: "emptyBodySizePlusOneTotalLinesPlusN",
    subKey: "empty",
    isNotVisiblebByDefault: true,
    couple: true,
    tooltipText:
      "In the proposed solution some of the lines are one size bigger than the original configuration. The plates have more lines that could be left empty.\n\nAll the parts must be ordered separately: empty plates, quick couplings, and electrical connectors, as mentioned in the datasheet.",
  },
];
const Results = ({ history, location }) => {
  const dispatch = useDispatch();
  const placcaCode = new URLSearchParams(location.search).get("placca");
  const isOperatorFromUrl = new URLSearchParams(location.search).get(
    "operator"
  );

  const [results, setResults] = useState(null);
  const [firstValidSetting, setFirstValidSetting] = useState("completePerfect");
  const [isConfigurationModalOpen, setIsConfigurationModalOpen] =
    useState(false);
  const circles = useSelector(({ circles }) => circles);
  const isOperator =
    useSelector(({ operator_mode }) => operator_mode) ||
    localStorage.getItem("operator_mode");

  const hasCirclesOneInchConfig = circles.find(
    (circle) => circle?.setting?.body_size.label === '1"'
  );

  useEffect(() => {
    if (!!isOperatorFromUrl) {
      dispatch(setOperatorMode());
    }
  }, [isOperatorFromUrl, dispatch]);

  useEffect(() => {
    if (placcaCode) {
      const decodedCircles = decode(placcaCode);

      setResults(findMatching(decodedCircles));
      dispatch(setReduxResults(findMatching(decodedCircles)));
    } else {
      if (circles.every((circle) => !circle)) {
        history.push("/");
      } else {
        const decodedCircles = findMatching(circles);
        setResults(decodedCircles);

        dispatch(setReduxResults(decodedCircles));
      }
    }
  }, [circles, history, dispatch, placcaCode]);
  useEffect(() => {
    if (placcaCode) {
      dispatch(setCircles(decode(placcaCode)));
    }
  }, [dispatch, placcaCode]);

  useEffect(() => {
    if (!!results) {
      const firstResult = Object.entries(results).find(
        (res) => !!res[1] && Object.keys(res[1]).length !== 0
      );
      setFirstValidSetting(!!firstResult ? firstResult[0] : "completePerfect");
    }
  }, [results]);

  const configurationCode = useMemo(() => {
    if (placcaCode) {
      return placcaCode;
    }
    const generatedCode = getCode(circles);
    return generatedCode || "-";
  }, [placcaCode, circles]);

  const filteredConfigurations = useMemo(() => {
    if (!results) {
      return [];
    }
    return resultsConfiguration
      .filter((config) =>
        hasCirclesOneInchConfig
          ? !config.isNotVisibleIfOneInch
          : !config.isNotVisiblebByDefault
      )
      .filter((config) =>
        !isOperator ? config.key === firstValidSetting : config
      );
  }, [results, hasCirclesOneInchConfig, isOperator, firstValidSetting]);

  const hasAvailableResults = useMemo(() => {
    if (!results || filteredConfigurations.length === 0) {
      return false;
    }

    return filteredConfigurations.some((config) => {
      const configResults = results?.[config.key]?.[config.subKey];

      if (Array.isArray(configResults)) {
        return configResults.length > 0;
      }

      return !!configResults;
    });
  }, [results, filteredConfigurations]);

  const shouldShowEmptyState = !!results && !hasAvailableResults;

  const closeConfigurationModal = () => setIsConfigurationModalOpen(false);
  const openConfigurationModal = () => setIsConfigurationModalOpen(true);

  const handleEditConfiguration = () => {
    closeConfigurationModal();
    history.push("/");
  };

  const handleBackToConfigurator = () => {
    history.push("/");
  };

  return (
    <div
      className={"results-view" + (history.action === "PUSH" ? " animate" : "")}
    >
      <Navbar
        title={
          <button
            type="button"
            className="results-view__nav-title-button"
            onClick={handleBackToConfigurator}
          >
            <Icon
              path={mdiArrowLeft}
              className="results-view__nav-title-icon"
            />
            Proposed Solution
          </button>
        }
        right={
          <button
            type="button"
            className="results-view__nav-link"
            onClick={openConfigurationModal}
          >
            View your configuration
          </button>
        }
      />

      <div className="results-view__content">
        <div className="results-view__list container">
          {shouldShowEmptyState ? (
            <div className="results-view__empty">
              No results available
            </div>
          ) : (
            filteredConfigurations.map((config) => (
              <div className="results-section" key={config.key}>
                <div className="results-section__title">
                  {config.label}
                  <TooltipInfo
                    text={config.tooltipText || config.label}
                    ariaLabel={`Info ${config.label}`}
                    placement="right"
                  />
                </div>
                <ResultsList
                  couple={config.couple}
                  results={
                    !!results &&
                    !!results[config.key] &&
                    results[config.key][config.subKey]
                  }
                />
              </div>
            ))
          )}

          <div className="results-view__footer">
            <button
              type="button"
              className="results-view__return-btn"
              onClick={handleBackToConfigurator}
            >
              Return to homepage
            </button>
          </div>
        </div>
      </div>

      {isConfigurationModalOpen && (
        <Popup cardWidth="50%" paddingCard="3rem" maxHeight="90vh">
          <div className="configuration-modal">
            <div className="configuration-modal__close-row">
              <button
                type="button"
                className="configuration-modal__close"
                aria-label="Close configuration recap"
                onClick={closeConfigurationModal}
              >
                <Icon path={mdiClose} size={2.5} />
              </button>
            </div>

            <div className="configuration-modal__header">
              <div>
                <p className="configuration-modal__title">
                  YOUR LINES CONFIGURATION
                </p>
                <p className="configuration-modal__code">
                  Configuration code: <span>{configurationCode || "-"}</span>
                </p>
              </div>

              <img
                src={logo}
                alt="Stucchi"
                className="configuration-modal__logo"
              />
            </div>

            <div className="configuration-modal__tables">
              {circles.find((value) => value?.type === "hydraulic") && (
                <Specs circles={circles} type="hydraulic" />
              )}

              {circles.find((value) => value?.type === "electric") && (
                <Specs circles={circles} type="electric" />
              )}
            </div>

            <button
              type="button"
              className="configuration-modal__cta"
              onClick={handleEditConfiguration}
            >
              Edit your configuration
            </button>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default Results;
